import React, { useState } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown, ChevronRight, Bot, Activity } from 'lucide-react';
import { ChildBotItem } from './ChildBotItem';
import { cn } from '@/lib/utils';

interface Bot {
  id: string;
  name: string;
  signal_token: string;
  type: string;
}

interface BotMapping {
  id: string;
  parent_bot_id: string;
  child_bot_id: string;
  is_active: boolean;
  notes: string | null;
  instrument: string | null;
  tp_usd: number | null;
  sl_usd: number | null;
  quantity: number | null;
  created_at: string;
  updated_at: string;
  parent_bot: Bot | null;
  child_bot: Bot | null;
}

interface ParentBotCardProps {
  parent: Bot;
  children: BotMapping[];
  activeCount: number;
  onToggleActive: (mapping: BotMapping) => void;
  onDelete: (mapping: BotMapping) => void;
  isUpdating: boolean;
  isDeleting: boolean;
  viewMode: 'grid' | 'list';
}

export const ParentBotCard: React.FC<ParentBotCardProps> = ({
  parent,
  children,
  activeCount,
  onToggleActive,
  onDelete,
  isUpdating,
  isDeleting,
  viewMode,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const getStatusVariant = () => {
    if (activeCount === 0) return 'secondary';
    if (activeCount === children.length) return 'default';
    return 'outline';
  };

  const getStatusText = () => {
    if (activeCount === 0) return 'Inactive';
    if (activeCount === children.length) return 'Active';
    return 'Partial';
  };

  const getStatusColor = () => {
    if (activeCount === 0) return 'border-muted';
    if (activeCount === children.length) return 'border-green-500/50 bg-green-500/5';
    return 'border-yellow-500/50 bg-yellow-500/5';
  };

  return (
    <Card className={cn('transition-all hover:shadow-md', getStatusColor())}>
      <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <div className="mt-1">
                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  )}
                </div>
                <div className="space-y-2 flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Bot className="h-4 w-4 text-primary shrink-0" />
                    <h3 className="font-semibold truncate">{parent.name}</h3>
                    <Badge variant="outline" className="text-xs">
                      {parent.type}
                    </Badge>
                  </div>
                  <div className="text-xs text-muted-foreground font-mono truncate">
                    {parent.signal_token}
                  </div>
                </div>
              </div>

              <Badge variant={getStatusVariant()} className="shrink-0">
                {getStatusText()}
              </Badge>
            </div>

            {/* Quick Stats */}
            <div className="flex items-center gap-4 mt-3 text-sm">
              <div className="flex items-center gap-1.5">
                <Activity className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="font-medium">{children.length}</span>
                <span className="text-muted-foreground">children</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="h-2 w-2 rounded-full bg-green-500" />
                <span className="font-medium text-green-600">{activeCount}</span>
                <span className="text-muted-foreground">active</span>
              </div>
              {activeCount !== children.length && (
                <div className="flex items-center gap-1.5">
                  <div className="h-2 w-2 rounded-full bg-muted-foreground" />
                  <span className="font-medium text-muted-foreground">
                    {children.length - activeCount}
                  </span>
                  <span className="text-muted-foreground">inactive</span>
                </div>
              )}
            </div>
          </CardHeader>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <CardContent className="pt-0 pb-4 px-4">
            <div className="space-y-2 border-t pt-3">
              {children.map((mapping) => (
                <ChildBotItem
                  key={mapping.id}
                  mapping={mapping}
                  onToggleActive={onToggleActive}
                  onDelete={onDelete}
                  isUpdating={isUpdating}
                  isDeleting={isDeleting}
                />
              ))}
            </div>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};
